import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createSprintRowByResponse,
  createProductBacklogItemRowByResponse,
  isProductBacklogItemRow,
  ProductBacklogItemRow,
  ProductBacklogRow,
  SprintProductItemDetail,
  SprintRow,
  UNSET_SPRINT_ROW,
  isUnsetSprintRowUuid,
  getSprintProductItemFromPbiRow,
} from '../productBacklog'
import {
  createWbsItemDeltaInputByRow,
  isWatcherEdited,
} from '../../../../lib/functions/wbsItem'
import store from '../../../../store'
import {
  doNotRequireSave,
  requireSave,
} from '../../../../store/requiredSaveData'
import { ProductBacklogEntity } from '../../../../domain/entity/ProductBacklogEntity'
import { useProductBacklogRepository } from '../../../../services/adaptors/productBacklogRepositoryAdaptor'
import _ from 'lodash'
import { generateUuid } from '../../../../utils/uuids'
import { SprintDetail } from '../../../../lib/functions/sprint'
import {
  SprintProductItemProp,
  UpdateSprintProductItemProps,
  updateSprintProductItems,
} from '../../../../lib/functions/sprintProductItem'
import { UpdateProductBacklogOutput } from '../../../../applications/ports/productBacklogRepository'
import { isTagColumnEdited } from '../../../../lib/functions/tag'

export const useProductBacklogData = (
  projectUuid: string,
  teamUuid: string | undefined,
  sprints: SprintDetail[]
): {
  data: ProductBacklogRow[]
  setData: (newData: ProductBacklogRow[]) => void
  refresh: () => Promise<void>
  save: (dataToSave?: ProductBacklogRow[]) => Promise<void>
  refreshSingleRow: (wbsItemUuid: string | undefined) => Promise<void>
  saveSingleRow: (uuid: string) => Promise<void>
  isLoading: boolean
} => {
  const [data, setDataInternal] = useState<ProductBacklogRow[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const originalData = useRef<Map<string, ProductBacklogItemRow>>(new Map())
  const [productBacklog, setProductBacklog] = useState<
    ProductBacklogEntity | undefined
  >()
  const {
    getByTeamUuid: getProductBacklogByTeamUuid,
    update: _updateProductBacklog,
  } = useProductBacklogRepository()

  const setData = useCallback((newData: ProductBacklogRow[]) => {
    setDataInternal(newData)
    store.dispatch(requireSave())
  }, [])

  const fetch = useCallback(async () => {
    if (!teamUuid) {
      setDataInternal([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)

      const productBacklog = await getProductBacklogByTeamUuid(teamUuid)
      setProductBacklog(productBacklog)

      const productBacklogItems = productBacklog?.productBacklogItems || []
      const productBacklogRows: ProductBacklogRow[] = []
      originalData.current.clear()

      // sprintsが未定義の場合は空配列を使用
      const teamSprints = sprints ? sprints.sort((a, b) => a.num - b.num) : []
      const unsetSprintRow: SprintRow = _.cloneDeep(UNSET_SPRINT_ROW)

      // SprintProductItem単位でマップを構築（同一スプリント内の複数担当者に対応）
      const spiMapBySprintUuid: Record<string, ProductBacklogItemRow[]> = {}
      const pbiMapByUuid: Record<string, ProductBacklogItemRow> = {}
      // 同一originalUuidの行間でsprintProductItemsを共有するためのマップ
      const sharedSprintProductItemsByOriginalUuid: Record<
        string,
        SprintProductItemDetail[]
      > = {}
      // 同一originalUuidの行間でwbsItemを共有するためのマップ
      // (PBI担当者、スプリントゴール、1スプリント当たり以外の列を連動させるため)
      const sharedWbsItemByOriginalUuid: Record<
        string,
        ProductBacklogItemRow['wbsItem']
      > = {}
      const addSpi = (sprintUuid: string, pbiRow: ProductBacklogItemRow) => {
        if (!spiMapBySprintUuid[sprintUuid]) {
          spiMapBySprintUuid[sprintUuid] = []
        }
        spiMapBySprintUuid[sprintUuid].push(pbiRow)
      }

      productBacklogItems.forEach(pbi => {
        const sprintProductItems = pbi.sprintProductItems || []

        // 同一originalUuid用の共有sprintProductItems配列を作成（まだない場合のみ）
        if (!sharedSprintProductItemsByOriginalUuid[pbi.uuid]) {
          sharedSprintProductItemsByOriginalUuid[pbi.uuid] =
            sprintProductItems.map(spi => ({
              uuid: spi.uuid,
              sprintUuid: spi.sprintUuid ?? unsetSprintRow.uuid,
              estimatedHour: spi.estimatedHour,
              taskEstimatedHour: spi.taskEstimatedHour,
              displayOrder: spi.displayOrder ?? 0,
              lockVersion: spi.lockVersion,
              assignee: spi.assignee,
              sprintGoal: spi.sprintGoal,
            }))
        }
        const sharedSprintProductItems =
          sharedSprintProductItemsByOriginalUuid[pbi.uuid]
        // 同一originalUuid用の共有wbsItemを取得（まだない場合はundefinedで、最初の行作成時に設定される）
        const sharedWbsItem = sharedWbsItemByOriginalUuid[pbi.uuid]

        if (sprintProductItems.length === 0) {
          // スプリント未設定
          const pbiRow = createProductBacklogItemRowByResponse(
            unsetSprintRow.uuid,
            pbi,
            undefined,
            sharedSprintProductItems,
            sharedWbsItem
          )
          // 最初の行のwbsItemを共有マップに保存
          if (!sharedWbsItemByOriginalUuid[pbi.uuid]) {
            sharedWbsItemByOriginalUuid[pbi.uuid] = pbiRow.wbsItem
          }
          addSpi(unsetSprintRow.uuid, pbiRow)
          pbiMapByUuid[pbi.uuid] = pbiRow
          originalData.current.set(pbi.uuid, _.cloneDeep(pbiRow))
          return
        }
        // 各SprintProductItemに対して処理（同じ共有配列・wbsItemを使用）
        sprintProductItems.forEach((spi, index) => {
          // index > 0 の場合は既に共有wbsItemが設定されている
          const currentSharedWbsItem = sharedWbsItemByOriginalUuid[pbi.uuid]
          // sprintUuidがnullの場合はスプリント未設定（explicit unset sprint SPI）
          const effectiveSprintUuid = spi.sprintUuid ?? unsetSprintRow.uuid
          const pbiRow = createProductBacklogItemRowByResponse(
            effectiveSprintUuid,
            pbi,
            spi,
            sharedSprintProductItems,
            currentSharedWbsItem
          )
          // 最初の行のwbsItemを共有マップに保存
          if (!sharedWbsItemByOriginalUuid[pbi.uuid]) {
            sharedWbsItemByOriginalUuid[pbi.uuid] = pbiRow.wbsItem
          }
          addSpi(effectiveSprintUuid, pbiRow)
          if (!pbiMapByUuid[pbiRow.originalUuid]) {
            pbiMapByUuid[pbiRow.originalUuid] = pbiRow
            originalData.current.set(pbiRow.originalUuid, _.cloneDeep(pbiRow))
          }
        })
      })

      // スプリントと成果物（PBI）行の親子関係を作る
      // Create pbi rows associated with team sprints.
      teamSprints.forEach(sprint => {
        const sprintRow = createSprintRowByResponse(sprint)
        productBacklogRows.push(sprintRow)

        const spiEntries = spiMapBySprintUuid[sprint.uuid] || []

        // SprintProductItem単位で行を作成
        const sortedPbiRows: ProductBacklogItemRow[] = [...spiEntries]

        const DEFAULT_DISPLAY_ORDER = 999999
        // displayOrderで昇順ソート
        // 行UUID（SprintProductItemのUUID）で検索して正確なdisplayOrderを取得
        sortedPbiRows.sort((a, b) => {
          const spiA = getSprintProductItemFromPbiRow(a, a.uuid)
          const spiB = getSprintProductItemFromPbiRow(b, b.uuid)
          const displayOrderA = spiA?.displayOrder ?? DEFAULT_DISPLAY_ORDER
          const displayOrderB = spiB?.displayOrder ?? DEFAULT_DISPLAY_ORDER
          return displayOrderA - displayOrderB
        })

        productBacklogRows.push(...sortedPbiRows)
      })

      // Create pbi rows unset sprint.
      const spiEntriesUnsetSprint =
        spiMapBySprintUuid[unsetSprintRow.uuid] || []

      // displayOrderで昇順ソート（通常スプリントと同じ）
      const sortedUnsetPbiRows = [...spiEntriesUnsetSprint]
      const DEFAULT_DISPLAY_ORDER_UNSET = 999999
      sortedUnsetPbiRows.sort((a, b) => {
        const spiA = getSprintProductItemFromPbiRow(a, a.uuid)
        const spiB = getSprintProductItemFromPbiRow(b, b.uuid)
        const displayOrderA = spiA?.displayOrder ?? DEFAULT_DISPLAY_ORDER_UNSET
        const displayOrderB = spiB?.displayOrder ?? DEFAULT_DISPLAY_ORDER_UNSET
        return displayOrderA - displayOrderB
      })

      productBacklogRows.push(unsetSprintRow)
      productBacklogRows.push(...sortedUnsetPbiRows)

      setDataInternal(productBacklogRows)
      store.dispatch(doNotRequireSave())
    } finally {
      setIsLoading(false)
    }
  }, [teamUuid, getProductBacklogByTeamUuid, sprints])

  const refresh = useCallback(async () => {
    await fetch()
  }, [fetch])

  useEffect(() => {
    fetch()
  }, [fetch])

  const updateProductBacklog = useCallback(
    async (
      allPbiRows: ProductBacklogItemRow[],
      editedPbiRows: ProductBacklogItemRow[],
      originalDataMap: Map<string, ProductBacklogItemRow>
    ): Promise<UpdateProductBacklogOutput | undefined> => {
      if (!productBacklog || editedPbiRows.length === 0) {
        return undefined
      }

      const allPbiRowsExcludingDuplicateRow = allPbiRows.filter(
        (v, index, self) =>
          self.findIndex(other => other.originalUuid === v.originalUuid) ===
          index
      )
      const watchers = editedPbiRows
        .filter((row: ProductBacklogItemRow) => {
          const original = originalDataMap.get(row.uuid)?.wbsItem.watchers ?? []
          const watchers = row.wbsItem.watchers ?? []
          return isWatcherEdited(original, watchers)
        })
        .map((row: ProductBacklogItemRow) => ({
          uuid: row.wbsItem.uuid!,
          wbsItemUuid: row.wbsItem.uuid!,
          userUuids: row.wbsItem.watchers?.map(watcher => watcher.uuid) || [],
        }))

      const tags = editedPbiRows
        .filter((row: ProductBacklogItemRow) => {
          const original = originalDataMap.get(row.uuid)?.wbsItem.tags ?? []
          const watchers = row.wbsItem.tags ?? []
          return isTagColumnEdited(original, watchers)
        })
        .map((row: ProductBacklogItemRow) => ({
          uuid: row.wbsItem.uuid!,
          wbsItemUuid: row.wbsItem.uuid!,
          tagUuids: row.wbsItem.tags?.map(tag => tag.uuid) || [],
        }))

      // assigneeUuid と sprintGoal は Sprint×PBI 単位のため SprintProductItem 経由で更新する
      const output = await _updateProductBacklog({
        productBacklog: {
          uuid: productBacklog.uuid,
          lockVersion: productBacklog.lockVersion,
          productBacklogItems: allPbiRowsExcludingDuplicateRow.map(pbiRow => ({
            uuid: pbiRow.originalUuid,
            deliverableUuid: pbiRow.wbsItem.uuid!,
          })),
          editedDeliverables: editedPbiRows.map(pbiRow =>
            createWbsItemDeltaInputByRow(
              originalDataMap.get(pbiRow.originalUuid)?.wbsItem,
              pbiRow.wbsItem
            )
          ),
        },
        watchers,
        tags,
      })
      return output
    },
    [productBacklog, _updateProductBacklog]
  )
  const updateSprints = useCallback(
    (
      editedPbiRows: ProductBacklogItemRow[],
      updateProductBacklogOutput: UpdateProductBacklogOutput | undefined,
      currentData: ProductBacklogRow[]
    ) => {
      if (editedPbiRows.length === 0) return
      const wbsItemLockVersionMap: Record<string, number> = {}
      // updateProductBacklogOutputがundefinedでない場合のみlockVersionを取得
      // （スプリントゴールやPBI担当者のみの編集の場合もundefinedになる可能性がある）
      updateProductBacklogOutput?.updateDeliverableOutputs?.forEach(output => {
        wbsItemLockVersionMap[output.uuid] = output.lockVersion
      })
      const request: UpdateSprintProductItemProps = {
        added: [],
        edited: [],
        deleted: [],
      }

      // 影響を受けるスプリントを収集
      const affectedSprintUuids = new Set<string>()

      // 1. 編集されたアイテムが所属するスプリント
      editedPbiRows.forEach(pbiRow => {
        const wbsItem = pbiRow.wbsItem
        const newSprints = wbsItem.sprints || []
        const oldSprints =
          originalData.current.get(pbiRow.originalUuid)?.wbsItem.sprints || []

        // 新旧両方のスプリントを影響対象に追加
        newSprints.forEach(s => affectedSprintUuids.add(s.uuid))
        oldSprints.forEach(s => affectedSprintUuids.add(s.uuid))

        // 現在の親スプリント（treeValue[0]）も影響対象に追加
        // sprintGoal や assignee の編集時にはここから取得する必要がある
        const parentSprintUuid = pbiRow.treeValue[0]
        if (parentSprintUuid) {
          if (!isUnsetSprintRowUuid(parentSprintUuid)) {
            affectedSprintUuids.add(parentSprintUuid)
          } else {
            // explicit null-sprint SPI がある場合のみ影響対象に追加
            const hasExplicitUnsetSpi = pbiRow.sprintProductItems?.some(
              spi => spi.uuid === pbiRow.uuid
            )
            if (hasExplicitUnsetSpi) {
              affectedSprintUuids.add(parentSprintUuid)
            }
          }
        }
      })

      const reorderedPbiRows: {
        [sprintUuid: string]: ProductBacklogItemRow[]
      } = {}
      currentData.forEach(row => {
        if (
          !isProductBacklogItemRow(row) ||
          !affectedSprintUuids.has(row.treeValue[0])
        ) {
          return
        }
        const parentSprintUuid = row.treeValue[0]
        if (!reorderedPbiRows[parentSprintUuid]) {
          reorderedPbiRows[parentSprintUuid] = []
        }
        reorderedPbiRows[parentSprintUuid].push(row)
      })

      // 影響対象スプリントの全アイテムを処理（兄弟全体の更新）
      affectedSprintUuids.forEach(sprintUuid => {
        const isUnset = isUnsetSprintRowUuid(sprintUuid)

        // そのスプリントに所属する全アイテムを取得（編集の有無に関わらず）
        const allItemsInSprint = reorderedPbiRows[sprintUuid] || []

        // スプリント内の全アイテムを処理
        allItemsInSprint.forEach((pbiRow, index) => {
          const deliverableUuid = pbiRow.wbsItem.uuid!
          const deliverableLockVersion =
            wbsItemLockVersionMap[deliverableUuid] ||
            pbiRow.wbsItem.lockVersion!
          // 該当行のSprintProductItemを取得
          // 行のUUID = SprintProductItemのUUID
          const currentSprintProductItem = pbiRow.sprintProductItems?.find(
            spi => spi.uuid === pbiRow.uuid
          )

          // implicit unset sprint（SPIが無い行）の場合、新規SPIを作成してdisplayOrderを保存
          if (isUnset && !currentSprintProductItem) {
            const displayOrder = index + 1
            request.added.push({
              uuid: generateUuid(),
              deliverableUuid,
              deliverableLockVersion,
              sprintUuid: null,
              estimatedHour: 0,
              displayOrder,
              assigneeUuid: undefined,
              sprintGoal: undefined,
            })
            return
          }

          // TODO: sprintEstimatedHour から取得するようにする？ rowDataに追加
          // goal やpbi担当者などと合わせるようにする
          const estimatedHour = currentSprintProductItem?.estimatedHour || 0

          // Sprint×PBI単位の assignee と sprintGoal を取得
          // SPI単位の担当者はsprintProductItemsから取得
          // assigneeがnullの場合は明示的にnullを渡す（担当者除去）
          const assigneeUuid =
            currentSprintProductItem?.assignee === null
              ? null
              : currentSprintProductItem?.assignee?.uuid
          const sprintGoal = pbiRow.sprintGoal

          // 既存のSprintProductItemかどうかを判定
          // 行のUUID（SprintProductItemのUUID）とsprintUuidの両方が一致する場合のみ既存として扱う
          // スプリント移動時は、元のSprintProductItemは削除され、新しいスプリント用に新規作成される
          const originalPbiRow = originalData.current.get(pbiRow.originalUuid)
          const editedSprintProductItem =
            originalPbiRow?.sprintProductItems?.some(
              spi => spi.uuid === pbiRow.uuid && spi.sprintUuid === sprintUuid
            )

          const displayOrder = index + 1
          // 既存のSprintProductItemのUUIDを取得（行のUUIDがSprintProductItemのUUID）
          // スプリント未設定の場合はAPIにnullを送信
          const input: SprintProductItemProp = {
            uuid: pbiRow.uuid,
            deliverableUuid,
            deliverableLockVersion,
            sprintUuid: isUnset ? null : sprintUuid,
            estimatedHour,
            displayOrder,
            assigneeUuid,
            sprintGoal,
          }
          if (editedSprintProductItem) {
            request.edited.push(input)
          } else {
            request.added.push(input)
          }
        })
      })

      // 削除されたSprintProductItemの処理
      editedPbiRows.forEach(pbiRow => {
        const wbsItem = pbiRow.wbsItem
        const oldPbiRow = originalData.current.get(pbiRow.originalUuid)
        const oldSpis = oldPbiRow?.sprintProductItems || []
        const newSpis = pbiRow.sprintProductItems
        const oldSpiUuids = oldSpis.map(s => s.uuid)
        const newSpiUuids = newSpis.map(s => s.uuid)

        const deletedSpiUuids = oldSpiUuids.filter(
          uuid => !newSpiUuids.includes(uuid)
        )
        deletedSpiUuids.forEach(deleteSpiUuid => {
          request.deleted.push({
            uuid: deleteSpiUuid,
            deliverableUuid: wbsItem.uuid!,
            deliverableLockVersion:
              wbsItemLockVersionMap[wbsItem.uuid!] || wbsItem.lockVersion!,
            sprintUuid: deleteSpiUuid,
          })
        })
      })

      if (
        request.added.length === 0 &&
        request.edited.length === 0 &&
        request.deleted.length === 0
      ) {
        return
      }
      return updateSprintProductItems(request)
    },
    []
  )

  const save = useCallback(
    async (dataToSave?: ProductBacklogRow[]) => {
      if (isLoading) return
      setIsLoading(true)
      const targetData = dataToSave ?? data
      try {
        const editedPbiRows = targetData.filter(
          v => isProductBacklogItemRow(v) && v.edited
        ) as ProductBacklogItemRow[]
        const output = await updateProductBacklog(
          targetData.filter(v => isProductBacklogItemRow(v)),
          editedPbiRows,
          originalData.current
        )
        // outputがundefinedでもupdateSprintsを呼ぶ
        // （スプリントゴールやPBI担当者のみの編集でも保存するため）
        await updateSprints(editedPbiRows, output, targetData)
      } finally {
        setIsLoading(false)
      }
    },
    [data, isLoading, updateProductBacklog, updateSprints]
  )

  const refreshSingleRow = useCallback(
    async (wbsItemUuid: string | undefined) => {
      // Can not refresh only single row because ProductBacklog has the order.
      await refresh()
    },
    [refresh]
  )
  const saveSingleRow = useCallback(
    async (uuid: string) => {
      // Can not save only single row because ProductBacklog has siblings to need to save all together.
      await save()
    },
    [save]
  )

  return {
    data,
    setData,
    refresh,
    save,
    refreshSingleRow,
    saveSingleRow,
    isLoading,
  }
}
